import { useCallback, useEffect, useState } from 'react';
import { hardwareApi, type HardwareStatus } from '../api/hardware';
import { useIsAdmin } from '../api/auth';

/** Settings → Hardware: device health, conveyor + rejector control, counters. */
export function HardwarePanel() {
  // Operators can see line status; only ADMIN may move machinery.
  const isAdmin = useIsAdmin();
  const [status, setStatus] = useState<HardwareStatus | null>(null);
  const [speed, setSpeed] = useState(300);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    hardwareApi
      .status()
      .then((s) => {
        setStatus(s);
        if (s.tracking) setSpeed(Math.round(s.tracking.speed_mm_s));
      })
      .catch(() => setStatus({ ok: false, error: 'unreachable' }));
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const confirmAct = (message: string, fn: () => Promise<void>) => {
    if (window.confirm(message)) void act(fn);
  };

  if (!status) return <div className="panel spinner">Loading hardware…</div>;

  if (!status.ok) {
    return (
      <div className="panel">
        <strong>Hardware (HAL)</strong>
        <div className="muted" style={{ marginTop: 10 }}>
          Service unreachable{status.error ? ` — ${status.error}` : ''}. Is{' '}
          <span className="kbd">TheVisionQC-hardware</span> running?
        </div>
      </div>
    );
  }

  const t = status.tracking;
  const running = t?.running ?? false;
  const armed = t?.armed ?? false;

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>Hardware — {status.health?.station}</strong>
        <span className={`badge ${status.health?.watchdog_ok ? 'good' : 'bad'}`}>
          {status.health?.watchdog_ok ? 'WATCHDOG OK' : 'WATCHDOG'}
        </span>
      </div>

      {/* Devices */}
      <div style={{ marginTop: 14 }}>
        {status.devices?.map((d) => (
          <div
            key={d.name}
            className="row"
            style={{ justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}
          >
            <span className="row" style={{ gap: 10 }}>
              <span className={`health-dot ${d.status === 'ok' ? 'ok' : 'bad'}`} />
              {d.name} <span className="muted">({d.role} · {d.driver})</span>
            </span>
            <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
              {d.status}
              {d.latency_ms != null ? ` · ${d.latency_ms.toFixed(1)}ms` : ''}
            </span>
          </div>
        ))}
      </div>

      {/* Counters */}
      {t && (
        <div className="row" style={{ marginTop: 14, gap: 10 }}>
          <Counter label="Triggered" value={t.metrics.triggered} />
          <Counter label="Rejected" value={t.metrics.rejected} tone="bad" />
          <Counter label="Passed" value={t.metrics.passed} tone="good" />
          <Counter label="Missed" value={t.metrics.missed_deadline} tone={t.metrics.missed_deadline ? 'bad' : undefined} />
        </div>
      )}

      {!isAdmin && (
        <p className="muted" style={{ fontSize: 12, marginTop: 14 }}>
          Sign in as ADMIN to control the conveyor and rejector.
        </p>
      )}

      {/* Conveyor control */}
      <div style={{ marginTop: 18, opacity: isAdmin ? 1 : 0.5 }}>
        <label>
          Conveyor — {running ? <span style={{ color: 'var(--good)' }}>running</span> : 'stopped'} ·{' '}
          encoder {t?.encoder ?? 0}
        </label>
        <div className="row">
          {!running ? (
            <button
              className="btn primary sm"
              disabled={busy || !isAdmin}
              onClick={() => confirmAct('Start the conveyor? Product will begin moving.', hardwareApi.conveyorStart)}
            >
              ▶ Start
            </button>
          ) : (
            <button className="btn sm" disabled={busy || !isAdmin} onClick={() => void act(hardwareApi.conveyorStop)}>
              ■ Stop
            </button>
          )}
          <input
            type="number"
            value={speed}
            min={1}
            max={5000}
            style={{ width: 90 }}
            onChange={(e) => setSpeed(Number(e.target.value))}
          />
          <span className="muted">mm/s</span>
          <button className="btn sm" disabled={busy || !isAdmin} onClick={() => void act(() => hardwareApi.conveyorSpeed(speed))}>
            Set speed
          </button>
        </div>
      </div>

      {/* Rejector control */}
      <div style={{ marginTop: 16, opacity: isAdmin ? 1 : 0.5 }}>
        <label>Rejector — {armed ? <span style={{ color: 'var(--good)' }}>armed</span> : 'disarmed'}</label>
        <div className="row">
          {!armed ? (
            <button className="btn sm" disabled={busy || !isAdmin} onClick={() => void act(hardwareApi.rejectorArm)}>
              Arm
            </button>
          ) : (
            <button className="btn sm" disabled={busy || !isAdmin} onClick={() => void act(hardwareApi.rejectorDisarm)}>
              Disarm
            </button>
          )}
          <button
            className="btn sm"
            disabled={busy || running || !isAdmin}
            title={running ? 'Stop the conveyor first' : 'Fire a test pulse'}
            onClick={() => confirmAct('Fire a rejector test pulse?', hardwareApi.rejectorTest)}
          >
            Test pulse
          </button>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          E-stop and safety circuits are hardware-only; VisionQC can never trigger or clear them.
        </p>
      </div>

      {error && <div style={{ color: 'var(--bad)', marginTop: 10 }}>{error}</div>}
    </div>
  );
}

function Counter({ label, value, tone }: { label: string; value: number; tone?: 'good' | 'bad' }) {
  const color = tone === 'good' ? 'var(--good)' : tone === 'bad' ? 'var(--bad)' : 'var(--text)';
  return (
    <div className="stat-tile" style={{ flex: 1, padding: '12px 14px' }}>
      <div className="stat-value" style={{ fontSize: 26, color }}>
        {value}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
